import type { GridColDef, GridValidRowModel } from '@mui/x-data-grid-pro';
import type { GridBlockType } from 'services/entities/BlockTypes';
import type { GridDeleteType, GridPositionChangedType, GridLayerChangedType, GridDimensionChangedType } from './saveHandlers';
import type { BillingFrequency, DiscountInput, DiscountType } from '@proposify/pricing-engine';
import { z } from 'zod';
import { trackError } from '@proposify/frontend-observability';

export enum TableAutoGeneratedRowType {
  AUTO_GENERATED = 'auto-generated',
  FLAT_GENERATED = 'flat-generated',
}

export enum TableTypeIdentifier {
  TEXT_TABLE = 'text_table',
  PRICING_TABLE = 'pricing_table',
}
export enum ColumnTypeIdentifier {
  SUBTOTAL = 'Subtotal',
  PRICE = 'Price',
  MULTIPLIER = 'Multiplier',
  TEXT = 'Text',
  HANDLER = 'Handler',
  OPTIONAL = 'Optional',
}

export enum TotalRowTypesIdentifier {
  SUBTOTAL = 'Subtotal',
  DISCOUNT = 'Discount',
  TAX = 'Tax',
  TOTAL = 'Total',
}

export enum RowTypesIdentifier {
  HEADER = 'Header',
  BODY = 'Body',
  FOOTER = 'Footer',
}

export type AllRowTypeIdentifier = TotalRowTypesIdentifier | RowTypesIdentifier;

export type TableMetadataType = {
  tableType: TableTypeIdentifier;
  pricingDecimalPlaces: number;
  tableTotal?: number;
  tableSubTotal?: number;
};

export type TableRowCellConfigExtraDataType = {
  valueFormatter?: string;
  hideCell?: boolean;
  isEditingActive?: boolean;
};

export interface TipTapDocument {
  type: 'doc';
  content: Array<{
    type: string;
    content?: any[];
    text?: string;
    [key: string]: any;
  }>;
}

export interface EnhancedCellValue {
  value: string | TipTapDocument;
  columnType: ColumnTypeIdentifier;
  discount?: { type: DiscountType; value: string | number };
  billingPayment?: string;
  billingFrequency?: string;
  // Formula-specific metadata
  isComputed?: boolean;
  formula?: string;
  dependencies?: string[];
  lastCalculated?: string;
}

export type CrmRowKind = 'auto-generated' | 'flat-generated';

export type CrmFieldGroups = { [chipName: string]: string[] };

export const crmRowMarkerFieldsSchema = z.object({
  crmRowKind: z.enum(['auto-generated', 'flat-generated']).optional(),
  crmSourceRowId: z.string().optional(),
  crmFieldGroups: z.record(z.array(z.string())).optional(),
});

export type CrmFieldInstances = {
  [template: string]: Array<{ name: string; friendlyName?: string; value?: unknown; objectId?: string; template?: string }>;
};

export interface TableRowType extends GridValidRowModel {
  cellConfig?: {
    [key: string]: TableRowCellConfigExtraDataType;
  };
  rowType: AllRowTypeIdentifier;
  isRowHidden?: boolean;
  isOptionalToggleAvailable?: boolean;
  isQtyEditable?: boolean;
  id: string;
  cells?: {
    [columnId: string]: EnhancedCellValue | string | boolean | any;
  };
  __reorder__: string;
  crmRowKind?: CrmRowKind;
  crmSourceRowId?: string;
  crmInstanceObjectId?: string;
  crmFieldGroups?: CrmFieldGroups;
  crmFieldInstances?: CrmFieldInstances;
  // Non-functional reference metadata only
  catalogItemId?: string;
}

export type TableColumnType = GridColDef & {
  columnType: ColumnTypeIdentifier;
  renderCell?: any;
  valueOptions?: string[];
  activeHoverColumn?: boolean;
  columnSelected?: boolean;
  width?: number;
};

export type TableType = {
  rows: TableRowType[];
  columns: TableColumnType[];
  totalRows?: TableRowType[] | null;
  metadata: TableMetadataType;
  selectedRow?: TableRowType | null;
  discounts?: DiscountInput[];
};

// The WS/persistence contract for a saved table: row markers are named here
// directly (crmRowKind, crmSourceRowId, crmFieldGroups) so the wire payload
// states them explicitly, rather than only inheriting them implicitly
// through TableRowType.
export type SaveTableContentType = Omit<TableType, 'rows'> & {
  rows: (TableRowType & {
    crmRowKind?: CrmRowKind;
    crmSourceRowId?: string;
    crmFieldGroups?: CrmFieldGroups;
  })[];
} & GridDeleteType;

export function assertCrmRowMarkersValid(rows: TableRowType[]): void {
  rows.forEach((row, index) => {
    const result = crmRowMarkerFieldsSchema.safeParse(row);
    if (!result.success) {
      trackError(new Error(`Invalid CRM row markers on row ${row.id ?? `at index ${index}`}: ${result.error.message}`), {
        errorFrom: 'editor-remote',
        feature: 'crm-table-rows',
      });
    }
  });
}

export type GridAddTableType = {
  blockId: string;
  type: GridBlockType;
  content: TableType;
  sectionId: string;
} & GridPositionChangedType &
  GridLayerChangedType &
  GridDimensionChangedType;

export interface EnhancedTableRowType extends TableRowType {
  billingFrequency?: BillingFrequency;
  discount?: DiscountInput | null;
  isRowOptional?: boolean;
  manualSubtotalOverride?: string | null;
  _pricingCalculation?: {
    originalSubtotal: string;
    discountAmount: string;
    discountedSubtotal: string;
    formattedSubtotal: string;
    includedInTotal: boolean;
    billingFrequency: BillingFrequency;
  };
}
